import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import { ProviderService, RegionService } from '@netfoundry-ui/shared/apiv2';

import { isEmpty, get, set, unset, debounce, cloneDeep, delay } from 'lodash';
import { ExtensionService, EDGE_ROUTER_EXTENSION_SERVICE, ManagementPermissionsService } from 'ziti-console-lib';
import { EdgeRouterV2 } from '@netfoundry-ui/shared/model';
import { ActivatedRoute, Router } from '@angular/router';
import { EdgeRoutersExtensionService } from './edge-routers-extension.service';
import {ApiService} from "@netfoundry-ui/shared/services";
import moment from "moment/moment";
import {ZitiShareService} from "@netfoundry-ui/feature/ziti/ziti-share";

type EdgeRouterTab = 'overview' | 'events' | 'metrics';
const VALID_EDGE_ROUTER_TABS: EdgeRouterTab[] = ['overview', 'events', 'metrics'];

@Component({
    selector: 'netfoundry-ui-edge-routers-extension',
    templateUrl: './edge-routers-extension.component.html',
    styleUrls: ['./edge-routers-extension.component.scss'],
    standalone: false
})
export class EdgeRoutersExtensionComponent implements OnInit {
    formData: any = {};
    isEditing = false;
    errors: any = {};
    alternateDomainName = '';
    linkListener: any = true;
    wssListener: any = false;
    nfRouterInit = false;
    hosting = false;
    currentNetwork: any = {};
    initNFRouterData = false;
    networkInit = false;
    initBuffer = true;
    loadingRouterData = true;
    private currentTabParam: EdgeRouterTab = 'overview';

    constructor(
        @Inject(EDGE_ROUTER_EXTENSION_SERVICE) public svc: EdgeRoutersExtensionService,
        private router: Router,
        private route: ActivatedRoute,
        private apiService: ApiService,
        private managementPermissions: ManagementPermissionsService,
    ) {
      this.networkInit = false;
      this.nfRouterInit = false;
      this.svc.resetNFRouterData();
      delay(() => {
        this.initBuffer = false;
      }, 500);
    }

    ngOnInit() {
        this.svc.zitiRouterData = {};
        this.route.paramMap.subscribe((params) => {
          const tab = params.get('tab') as EdgeRouterTab;
          this.currentTabParam = VALID_EDGE_ROUTER_TABS.includes(tab) ? tab : 'overview';
        });
        this.apiService.currentNetwork.subscribe((network) => {
          this.currentNetwork = network;
          this.networkInit = true;
          if (this.initNFRouterData) {
            this.loadNFRouter();
            this.initNFRouterData = false;
          }
        });
        this.svc.zitiRouterChanged.subscribe((data) => {
            this.formData = data;
            this.isEditing = !isEmpty(this.formData?.id);
            this.svc.formDataChanged.next(this.formData);
            if (this.isEditing) {
                if (!this.currentNetwork?.id) {
                  this.initNFRouterData = true;
                  return;
                }
                this.loadNFRouter();
            } else {
                this.formData.noTraversal = true;
                if (!this.nfRouterInit) {
                    this.svc.resetNFRouterData();
                    this.nfRouterInit = true;
                    this.loadingRouterData = false;
                }
            }
        });
        this.svc.closed.subscribe(() => {
            this.svc.hosting = false;
            this.svc.newRouter = false;
            this.svc.nfHosted = false;
            this.svc.resetNFRouterData();
            this.svc.errors = {};
            this.nfRouterInit = false;
        });
    }

    get hasEnrollmentJwt() {
      return !isEmpty(this.svc.zitiRouterData?.enrollmentJwt);
    }

    get enrollmentExpired() {
      return this.svc?.zitiRouterData?.enrollmentExpiresAt && moment(this.svc?.zitiRouterData?.enrollmentExpiresAt).isBefore()
    }

    loadNFRouter() {
      this.svc.resetNFRouterData();
      if (isEmpty(this.svc.zitiRouterData.id)) {
        return;
      }
      this.loadingRouterData = true;
      this.svc.getNFHostedEdgeRouter().then((result) => {
        this.svc.nfRouterData = result || cloneDeep(this.svc.DEFAULT_NF_ROUTER_DATA);
        this.svc.nfHosted = this.svc.nfHosted || !isEmpty(this.svc?.nfRouterData?.zitiId);
        this.nfRouterInit = true;
        if (this.svc.inProcess) {
          this.svc.pollRouterStatus();
        }
      }).finally(() => {
        this.loadingRouterData = false;
      });
    }

    get showProvider() {
      return this.svc?.nfRouterData?.provider !== 'CUSTOMER'
    }
    get focusNfHosting() {
        return this.svc.newRouter && this.svc.nfHosted;
    }

    goToProcesses() {
        this.router.navigate(['/process-executions']);
    }

    editHosting() {
        if (this.disableHostButton) {
            return;
        }
        this.svc.nfHosted = true;
        this.svc.confirmNFHosting();
    }

    get hostAreaToolTip() {
        if (this.disableHostPod) {
            return 'Router must be saved first before setting up hosting';
        } else {
            return '';
        }
    }

    get useStrictReadonlyForm(): boolean {
        return this.managementPermissions.useStrictReadonlyFormUi('edge-routers', false);
    }

    get hostButtonToolTip() {
        if (this.managementPermissions.useStrictReadonlyFormUi('edge-routers', false)) {
            return this.managementPermissions.saveDeniedTooltip;
        }
        if (this.svc.inProcess) {
            return 'Can not edit router hosting while host configuration is updating';
        }
        return '';
    }

    get showLoader() {
      return !this.networkInit || !this.nfRouterInit || this.initBuffer || this.loadingRouterData;
    }

    get showHostingConfig() {
        return this.svc?.nfRouterData?.id;
    }

    get disableHostPod() {
        return isEmpty(this.svc?.zitiRouterData?.id) && !this.svc?.newRouter;
    }

    get disableHostButton() {
        return (isEmpty(this.svc?.zitiRouterData?.id) && !this.svc?.newRouter) || this.svc.inProcess;
    }

    get isNFHosted() {
        return !isEmpty(this.svc?.nfRouterData?.zitiId && !this.svc.newRouter) || this.svc.nfHosted;
    }

    set isNFHosted(isHosted) {
        this.svc.nfHosted = isHosted;
    }

    get disableHostForm() {
        return isEmpty(this.svc?.zitiRouterData?.id) && isEmpty(this.svc?.nfRouterData?.zitiId);
    }

    get showUpdating() {
        return this.svc?.inProcess;
    }

    get showEntityTabs() {
        return !isEmpty(this.svc?.zitiRouterData?.id);
    }

    get entityId(): string {
        return this.route.snapshot.paramMap.get('id') || '';
    }

    get activeTab(): EdgeRouterTab {
        return this.showEntityTabs ? this.currentTabParam : 'overview';
    }

    get entityTabData() {
        if (this.activeTab === 'metrics') {
            return {
                resourceType: 'edge-router',
                model: this.svc?.zitiRouterData,
                networkId: this.currentNetwork?.id,
                networkGroupId: this.currentNetwork?.networkGroupId,
            };
        }
        return {
            resourceType: 'edgeRouter',
            model: { ...this.svc?.zitiRouterData, nfId: this.svc?.nfRouterData?.id },
            networkId: this.currentNetwork?.id,
            networkGroupId: this.currentNetwork?.networkGroupId,
        };
    }
}
